import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";
import { pluralize } from "@/shared/text";

/**
 * Whether the context carries no meaningful data
 * (no datasets, no files, no visualizer state).
 */
export function isEmptyContext(ctx: ClientViewingContext): boolean {
  return (
    ctx.dataset_ids.length === 0 &&
    ctx.file_ids.length === 0 &&
    (ctx.visualizer_state === undefined || ctx.visualizer_state === null)
  );
}

/**
 * Extract the number of panels from a visualizer state object, or 0
 * if the structure is unexpected.
 */
export function getVisualizerPanelCount(
  state: Record<string, unknown>,
): number {
  if (
    typeof state === "object" &&
    state !== null &&
    "panels" in state &&
    typeof state.panels === "object" &&
    state.panels !== null
  ) {
    return Object.keys(state.panels).length;
  }
  return 0;
}

/**
 * Build a short human-readable summary of a context, e.g.
 * "Context: 2 datasets, 1 file, visualizer" or "Context cleared".
 */
export function buildContextSummary(ctx: ClientViewingContext): string {
  const parts: string[] = [];

  if (ctx.dataset_ids.length > 0) {
    const n = ctx.dataset_ids.length;
    parts.push(`${n} ${pluralize("dataset", n)}`);
  }
  if (ctx.file_ids.length > 0) {
    const n = ctx.file_ids.length;
    parts.push(`${n} ${pluralize("file", n)}`);
  }
  if (ctx.visualizer_state) {
    parts.push("visualizer");
  }

  if (parts.length === 0) {
    return "Context cleared";
  }

  return `Context: ${parts.join(", ")}`;
}
